import type { LayerPrefs } from './prefs';
import type { LayerKey } from '@tsr/shared-types';
import { AREA_COMMAND_HEADING } from './incidentVocabulary';

/**
 * Layer Architecture (Phase L.2) — the doctrinal multi-hazard layer-group model.
 *
 * Declares the seven operator-toggleable layer groups (🎯 Default / 🔥 Fire Risk
 * / ⚠️ Hazard / 🌊 Flood Risk / 🧊 Ice Storm Risk / 🚒 Asset / 🛰️ Context) per the
 * locked L.2 architecture (build.md § L.2) and the L.0 default-view spec. The
 * LayerToggle component renders from this model; App/MapView consume the boolean
 * LayerPrefs fields the rows drive.
 *
 * L.2 dissolved the old "Weather" group: Fire Weather Index + Reported Wildfires
 * moved to the new Fire Risk group, and Extreme Weather Warnings moved to Hazard.
 * The new layout is multi-select throughout — the FWI↔Wildfires radio exclusivity
 * (an artefact of the old Weather group) is gone, so an operator can stack the
 * FWI heatmap, fire markers, and hazard signals together (the "see everything"
 * multi-hazard doctrine).
 *
 * Placeholder layers (`comingInPhase` set, no `prefKey`/`layerKey`) render as
 * disabled "Coming in Phase X" rows that never toggle or audit (L.1.5 #4) — their
 * real data ships in the named phase.
 */

/** The boolean fields of LayerPrefs (excludes the array fields). */
export type BooleanPrefKey = {
  [K in keyof LayerPrefs]: LayerPrefs[K] extends boolean ? K : never;
}[keyof LayerPrefs];

export type GroupSelection = 'multi' | 'radio';
export type GroupId =
  | 'default'
  | 'fire-risk'
  | 'hazard'
  | 'flood-risk'
  | 'ice-storm-risk'
  | 'advisories'
  | 'asset'
  | 'context';

export interface LayerDef {
  /** Stable server layer_key — also the audit payload `layer` and persistence
   *  key. Absent for placeholder rows (nothing to persist). */
  layerKey?: LayerKey;
  /** The LayerPrefs boolean field this row drives. Absent for placeholders. */
  prefKey?: BooleanPrefKey;
  label: string;
  /** Tailwind background class for the swatch dot. */
  swatchClass: string;
  title?: string;
  /** Set → disabled "Coming in Phase X" placeholder (e.g. "E.F"). */
  comingInPhase?: string;
  /** Marks the row that carries a special nested sub-toggle section (Ontario Corps
   *  partners, aircraft roles, camera smoke-priority filter, the Road Closures
   *  "Restrictions" mute, or the community "Vulnerability shading" add). */
  special?: 'ontarioCorps' | 'liveAircraft' | 'cameras' | 'roadClosures' | 'communities';
  /** In a `radio` group, exempt this row from radio exclusivity — it neither
   *  turns off siblings when enabled, nor is turned off when a sibling is
   *  enabled. Used by the Extreme Weather Warnings MUTE toggle, which lives in
   *  the Weather group visually but is orthogonal to the FWI↔All-Wildfires
   *  radio choice (it mutes a Layer #2 subset, not a Weather map-fill). */
  radioExempt?: boolean;
}

export interface LayerGroupDef {
  id: GroupId;
  /** Emoji prefix shown in the group header. */
  icon: string;
  label: string;
  /** 'radio' → only one real layer on at a time (Weather). */
  selection: GroupSelection;
  /** L.0 Default group is non-collapsible (always visible). */
  collapsible: boolean;
  defaultCollapsed: boolean;
  /** Short doctrinal note shown under the header for the Default group. */
  note?: string;
  layers: LayerDef[];
}

export const LAYER_GROUPS: ReadonlyArray<LayerGroupDef> = [
  {
    id: 'default',
    icon: '🎯',
    label: 'Default',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: false, // baseline COP — open by default (L.2)
    note: 'Baseline COP — on at country zoom',
    layers: [
      {
        layerKey: 'live-video-feeds',
        prefKey: 'cameras',
        label: 'Live Video Feeds',
        // Law 1 reserved LIVE-GREEN (ui.COVERAGE_LIVE) — the exact family
        // token, not a Tailwind near-miss (#261 sweep).
        swatchClass: 'bg-[#22c55e]',
        title: 'Live camera feeds — green pulsing dots at country zoom (L.0 layer 1)',
        special: 'cameras',
      },
      {
        layerKey: 'aircraft',
        prefKey: 'liveAircraft',
        label: 'Aircraft & Marine',
        swatchClass: 'bg-cyan-400',
        title: 'Live ADS-B aircraft + AIS marine vessels (marine ingest lands in R.1d)',
        special: 'liveAircraft',
      },
      {
        layerKey: 'reported-oc-wildfires',
        prefKey: 'reportedOcWildfires',
        label: 'Reported OC Wildfires',
        // Law 2(c) OC-FUL flame #e25822 (fireStages.OC_FUL_COLOR) — the old
        // bg-red-600 taught the revoked pre-Law-5 #dc2626 (#261 sweep).
        swatchClass: 'bg-[#e25822]',
        // stage = OC across EVERY response posture (Full flame · Modified/Monitored orange ·
        // response-not-reported ember) — NOT full-response-only (#621 un-narrowed the fetch;
        // the client LOD, not a response_type filter, governs which OC fires render). L.0 layer 4.
        title:
          'Out-of-control (OC) wildfires, all response postures — Full / Modified / Monitored / not-reported; the LOD governs visibility (L.0 layer 4)',
      },
      {
        layerKey: 'urgent-anomalies',
        prefKey: 'urgentAnomalies',
        label: 'Urgent Alerts',
        // The toggle swatch is the surface's salience identity — red (the most-urgent L4
        // + the CAP public alerts that also ride this surface). icon-urgency council
        // 2026-07-05 (R1): the AI-fused hotspot dots on this surface FILL by the machine-
        // derived 1–4 severity (L1 slate · L2 blue · L3 amber · L4 red) — the per-level KEY
        // renders under this row (LayerToggle ThreatLevelKey, relabelled Threat by #613).
        // CAP stays the single map red until Phase 2, so the swatch (and this multi-hazard
        // row) is NOT relabelled per-level.
        swatchClass: 'bg-[#ff3b30]',
        title:
          'Multi-hazard alert surface — AI-fused hotspots + NPAS / Alert Ready (CAP) public alerts; every red dot needs triage (L.0 layer 2)',
      },
    ],
  },
  {
    id: 'fire-risk',
    icon: '🔥',
    label: 'Fire Risk',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: false, // most commonly toggled fire layers → starts open
    layers: [
      {
        layerKey: 'fire-weather-index',
        prefKey: 'fwiHeatmap',
        label: 'Fire Weather Index',
        swatchClass: 'bg-orange-400',
        title:
          'CWFIS Fire Weather Index heatmap (public:fwi_current, rendered as the government displays it)',
      },
      {
        layerKey: 'all-reported-wildfires',
        prefKey: 'allReportedWildfires',
        label: 'Reported Wildfires (all stages)',
        swatchClass: 'bg-orange-500',
        title:
          'Every reported wildfire, full colour system — OC-FUL red flame, OC-Monitored orange, Being Held yellow, Under Control blue',
      },
      {
        // Hot Spots — raw PRE-FUSION satellite thermal detections (E.W.0.5). Went
        // live: ambient source-coloured dots below the F.1 fusion layer. Default
        // OFF — it is a FIRE-RISK layer, not one of the four DEFAULT_VIEW_LAYERS
        // (prefs.ts), so it never starts on. RawHotspotsLayer additionally gates
        // rendering to regional+ zoom when the operator does toggle it on.
        layerKey: 'raw-hotspots',
        prefKey: 'rawHotspots',
        label: 'Hot Spots',
        swatchClass: 'bg-amber-400',
        title:
          'Raw pre-fusion satellite thermal anomalies (FIRMS / CWFIS / GOES-R) — ambient, source-coloured, visible regional+ zoom',
      },
      {
        // Fusion Candidates (#370 de-flood) — the POST-fusion below-urgent mass
        // (Threat Level 1/2 high_risk_hotspots, #613) the density-arm gate demoted
        // off the Urgent Alerts surface. Muted anomaly dots in the reserved
        // URGENT-RED family (size/alpha encode the level, never pulsing), default
        // OFF; FusionCandidatesLayer gates rendering to regional+ zoom. Click →
        // the same High-Risk Hotspot side panel as an urgent red.
        layerKey: 'fusion-candidates',
        prefKey: 'fusionCandidates',
        label: 'Fusion Candidates (below urgent)',
        // Same reserved URGENT-RED family as the Urgent Alerts dots (#261) — these
        // ARE fused anomalies, just below the urgent threshold; the muted size/alpha
        // (not hue) is what reads them apart, per §3B.8.
        swatchClass: 'bg-[#ff3b30]',
        title:
          'Below-urgent AI-fused hotspots (Threat Level 1–2) the density gate demoted off Urgent Alerts — muted, never pulsing, visible regional+ zoom',
      },
      {
        // Camera Smoke Detections — pre-fusion camera-CV smoke verdicts (E.W.5),
        // the camera-CV sibling of Hot Spots. Ambient amber camera-glyph dots,
        // default OFF, visible regional+ zoom. Distinct from the smoke_detected
        // Anomaly-Queue dot (that act-register surface is untouched).
        layerKey: 'camera-smoke-detections',
        prefKey: 'cameraSmoke',
        label: 'Camera Smoke Detections',
        // Law 4 ambient retune (cameraSmoke.CAMERA_SMOKE_COLOR #c69a5e) — the
        // old bg-amber-500 taught the vivid pre-retune amber (#261 sweep).
        swatchClass: 'bg-[#c69a5e]',
        title:
          'Pre-fusion camera-CV smoke detections (Claude-vision confirm-stage) — ambient amber camera glyphs, visible regional+ zoom',
      },
      // #262 (founder ruling) — the "Camera Line-of-Sight (modelled)" entry that
      // lived here is REMOVED: the #178 viewshed is now part of what a camera IS
      // (auto-drapes on selection, released on deselect — see prefs.viewshedOnSelect
      // and the camera panel's "hide viewshed" affordance). PART IX: contextual
      // render beats a standing toggle.
      {
        // Fire Bans & Restrictions — went live (#213): the Alberta Fire Ban System
        // (Advisory → Restriction → Ban → Forest-Area Closure) + Fire Control Orders
        // from the arcgis_public_features `fire_restriction` category, via the
        // generalized config-driven arcgis renderer. A burnt-amber/charred-brown
        // restriction ramp distinct from the active-fire reds. Default OFF; self-gates
        // to regional+ zoom.
        layerKey: 'ab-fire-bans',
        prefKey: 'fireBans',
        label: 'Fire Bans & Restrictions',
        swatchClass: 'bg-amber-800',
        title:
          'Alberta fire bans & restrictions (Advisory / Restriction / Ban / Forest-Area Closure / Control Order) — regulatory prohibition areas, visible regional+ zoom',
      },
    ],
  },
  {
    id: 'hazard',
    icon: '⚠️',
    label: 'Hazard',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        // EWW — fully functional MUTE toggle on the Urgent Alerts CAP-polygon
        // subset. Effective visibility is `urgentAnomalies && extremeWeatherWarnings`:
        // muting it hides the CAP polygons while the AI-fused hotspots remain.
        layerKey: 'extreme-weather-warnings',
        prefKey: 'extremeWeatherWarnings',
        label: 'Extreme Weather Warnings',
        swatchClass: 'bg-yellow-400',
        title: 'Mute the CAP polygon overlay on Urgent Alerts (AI hotspots remain)',
      },
      {
        // Evacuation Orders & Alerts — went live (#204): GeoBC / Emergency
        // Management BC order & alert AREA polygons from the arcgis_public_features
        // ingest (mig 0063). Order (mandatory leave) vs alert (be ready), with
        // affected population/homes in the panel. Default OFF (NOT a Default-view
        // layer — respects the layer lock); EvacuationOrdersLayer gates rendering to
        // regional+ zoom so the national view stays clean.
        layerKey: 'bc-evacuation-orders',
        prefKey: 'evacuationOrders',
        label: 'Evacuation Orders & Alerts',
        swatchClass: 'bg-violet-600',
        title:
          'GeoBC / EMBC evacuation order & alert areas (affected population/homes) — order vs alert, visible regional+ zoom',
      },
      {
        // #445-(3) — OPERATOR-DECLARED evac orders recorded in-COP on behalf of a
        // named issuing authority. A PARALLEL layer to the external GeoBC one above
        // (same violet register, distinct source). Default OFF; self-gates regional+.
        layerKey: 'operator-evacuation-orders',
        prefKey: 'operatorEvacuationOrders',
        label: 'Evacuation Orders — recorded in-COP',
        swatchClass: 'bg-violet-600',
        title:
          'Operator-declared evacuation orders & alerts recorded in-COP on behalf of a named issuing authority (municipal / First Nation / province) — order vs alert, visible regional+ zoom',
      },
      {
        label: 'Air Quality (AQHI)',
        swatchClass: 'bg-purple-400',
        comingInPhase: 'E.AQ',
        title: 'ECCC AQHI air-quality index (ingested in E.1.5; render coming soon)',
      },
      {
        label: 'Earthquakes',
        swatchClass: 'bg-amber-600',
        comingInPhase: 'E.EQ',
        title: 'Significant seismic events (FDSN feed ingested; render coming soon)',
      },
      // #159 — the former "Public Alerts (AMBER / civil emergency)" placeholder was
      // retired: NAAD/CAP public alerts (incl. AMBER + civil emergency) already
      // render in the Urgent Alerts surface via the Extreme Weather Warnings mute.
      // One alert surface, not two.
    ],
  },
  {
    id: 'flood-risk',
    icon: '🌊',
    label: 'Flood Risk',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        // Hydrometric Gauges — went live (E.F.1): the first render of the WSC
        // hydrometric_observations ingest. Ambient gauge dots coloured by the
        // rate-of-rise band (TSR-derived — no official Canadian flood stage).
        // Default OFF; HydrometricGaugesLayer self-gates to regional+ zoom so the
        // national view never clutters. Replaced the "Coming in Phase E.F" placeholder.
        layerKey: 'hydrometric-gauges',
        prefKey: 'hydrometricGauges',
        label: 'Hydrometric Gauges',
        swatchClass: 'bg-blue-400',
        title:
          'Live WSC river-gauge levels coloured by rate-of-rise (TSR-derived; no official flood stage). Ambient, visible regional+ zoom.',
      },
      {
        // #177 Cesium-wow — 3D Flood Inundation (modelled). World Terrain + OSM
        // Buildings + a settable translucent water surface that shows which
        // buildings flood. A MODELLED what-if (not an official inundation product);
        // renders only in the bounded Kashechewan / Albany River demo region. Default
        // OFF. The on-map control (level slider + gauge snap) appears when enabled.
        layerKey: 'flood-inundation',
        prefKey: 'floodInundation',
        label: '3D Flood Inundation (modelled)',
        swatchClass: 'bg-blue-500',
        title:
          'MODELLED what-if: extrude a water surface over 3D terrain + buildings to a settable level (manual or snapped from the upstream gauge) and see which buildings flood. Demo region only (Kashechewan / Albany River). Not an official inundation product.',
      },
      {
        // #212 — NRCan EGS "Active Floods in Canada" observed flood-EXTENT raster
        // as an official WMS overlay (layer 0). Client-side, no ingest; the
        // authoritative observed counterpart to the modelled inundation above.
        // Ambient register (opacity ≤0.55). Default OFF; near-real-time, off-season
        // empty (honest).
        layerKey: 'nrcan-flood-extent',
        prefKey: 'nrcanFloodExtent',
        label: 'Flood Extent (NRCan EGS)',
        swatchClass: 'bg-sky-500',
        title:
          'Natural Resources Canada Emergency Geomatics Service — observed flood-extent polygons from satellite imagery (near-real-time). The authoritative observed counterpart to the modelled inundation. Ambient WMS overlay; off-season empty.',
      },
      {
        // W.2 — went live: ambient community markers coloured by wellness status,
        // with headcount + wellness editor in the side panel. Shares the same
        // /api/communities data as the Ice Storm "Vulnerable Communities" row.
        layerKey: 'flood-vulnerable-communities',
        prefKey: 'floodVulnerableCommunities',
        label: 'Flood-Vulnerable Communities',
        swatchClass: 'bg-blue-300',
        title:
          'Communities with headcount + wellness-check tracking (W.2). House glyph coloured by wellness status.',
        special: 'communities',
      },
      {
        label: 'Precipitation Forecast',
        swatchClass: 'bg-sky-400',
        comingInPhase: 'E.F',
        title: 'Heavy-rain / precipitation forecast overlay',
      },
    ],
  },
  {
    id: 'ice-storm-risk',
    icon: '🧊',
    label: 'Ice Storm Risk',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        label: 'Freezing Rain Forecast',
        swatchClass: 'bg-sky-300',
        comingInPhase: 'E.I',
        title: 'Freezing-rain / ice-accretion forecast overlay',
      },
      {
        // W.2 — same community layer as Flood Risk, surfaced under Ice Storm for
        // group affinity (warming-centre / vulnerable-resident reach is the
        // ice-storm framing, §4.5). Both toggles back the same data.
        layerKey: 'ice-storm-vulnerable-communities',
        prefKey: 'iceStormVulnerableCommunities',
        label: 'Vulnerable Communities',
        swatchClass: 'bg-sky-200',
        title:
          'Communities with wellness-check + headcount tracking (W.2) — warming centres, vulnerable-resident reach.',
        special: 'communities',
      },
      {
        // Power Outages — went live: the first render of the E.1.9/E.1.10
        // utility_outages ingest (4 utilities + municipal LDCs). Ambient
        // status-coloured dots sized by customers-affected, with ground-clamped
        // service-area polygons where published. Default OFF; PowerOutagesLayer
        // gates rendering to regional+ zoom so the national view stays clean.
        layerKey: 'ice-storm-power-outages',
        prefKey: 'powerOutages',
        label: 'Power Outages',
        swatchClass: 'bg-rose-400',
        title:
          'Live utility power outages (BC Hydro / Hydro-Québec / Hydro One / NS Power + municipal LDCs) — ambient, sized by customers affected, visible regional+ zoom',
      },
      {
        // Road Closures — went live (R.1f): the render of the road_closures ingest
        // (Ontario 511 + Alberta 511 via Castle-Rock REST, DriveBC via Open511).
        // Ambient register-coloured dots (full closure vs restriction) anchoring a
        // ground-clamped affected-segment line. Default OFF; RoadClosuresLayer gates
        // rendering to regional+ zoom so the national view stays clean.
        layerKey: 'ice-storm-road-closures',
        prefKey: 'roadClosures',
        label: 'Road Closures',
        swatchClass: 'bg-orange-700',
        title:
          'Live road closures + restrictions (Ontario 511 / Alberta 511 / DriveBC) — ambient, closure vs restriction, visible regional+ zoom',
        // Carries the "Restrictions" mute sub-toggle (L.1 mute pattern): layer ON +
        // restrictions OFF = full closures only.
        special: 'roadClosures',
      },
      {
        // Estimated Comms Degradation — went live (#109): the F.1 fusion-derived
        // ESTIMATE zone (buffered active-outage cluster under a cell-tower
        // battery-decay model, corroborated by freezing-rain CAP + the #108 telecom
        // ASN tripwire). Ambient translucent indigo polygon, clearly labelled
        // "estimated", never pulses. Default OFF; CommsDegradationZonesLayer gates
        // rendering to regional+ zoom so the national view stays clean.
        layerKey: 'ice-storm-comms-degradation',
        prefKey: 'commsDegradationZones',
        label: 'Estimated Comms Degradation',
        swatchClass: 'bg-indigo-400',
        title:
          'ESTIMATE — probable cell-availability-loss zones derived from outage clusters + battery-decay + telecom tripwire. Not measured coverage; blind to localized tower loss. Visible regional+ zoom.',
      },
    ],
  },
  {
    // 📋 Advisories & Declarations (#213) — the remaining ingested public-ArcGIS
    // category feeds surfaced via the generalized config-driven renderer: governance
    // declarations + slow-moving advisory conditions that aren't a single hazard
    // source. All default OFF; each self-gates to regional+ zoom.
    id: 'advisories',
    icon: '📋',
    label: 'Advisories & Declarations',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        layerKey: 'bc-state-of-local-emergency',
        prefKey: 'stateOfLocalEmergency',
        label: 'State of Local Emergency',
        swatchClass: 'bg-fuchsia-700',
        title:
          'BC State of Local Emergency & Band Council Resolutions — the "who has declared an emergency" layer (GeoBC). Magenta declaration boundary, visible regional+ zoom.',
      },
      {
        layerKey: 'drought-conditions',
        prefKey: 'droughtConditions',
        label: 'Drought Conditions',
        swatchClass: 'bg-yellow-700',
        title:
          'BC drought-level basins + Manitoba river/lake flow-class stations — dry brown/tan by severity, visible regional+ zoom.',
      },
      {
        layerKey: 'boil-water-advisories',
        prefKey: 'boilWaterAdvisories',
        label: 'Boil Water Advisories',
        swatchClass: 'bg-cyan-700',
        title:
          'Manitoba boil-water advisories (public water systems) — cyan dots by timeframe to resolve, visible regional+ zoom.',
      },
      {
        layerKey: 'tropical-cyclones',
        prefKey: 'tropicalCyclones',
        label: 'Tropical Cyclones',
        swatchClass: 'bg-sky-700',
        title:
          'Active tropical-cyclone forecast positions + error cone (US-NHC, the live vector source for ECCC’s Canadian Hurricane Centre webmap) — off Atlantic-season most of the year. Visible regional+ zoom.',
      },
      {
        // #212 — ECCC GeoMet hurricane forecast TRACK LINE as an official WMS
        // raster overlay (HURRICANE_LINE). Client-side, no ingest; pairs with the
        // #217 NHC vector track above (this is ECCC's own render). Ambient register
        // (opacity ≤0.55). Default OFF; off Atlantic-season most of the year.
        layerKey: 'eccc-hurricane-track',
        prefKey: 'ecccHurricaneTrack',
        label: 'Hurricane Forecast Track (ECCC)',
        swatchClass: 'bg-sky-600',
        title:
          'Environment and Climate Change Canada (GeoMet) hurricane forecast track line — the official ECCC raster render, complementing the NHC vector track. Ambient WMS overlay, off Atlantic-season most of the year.',
      },
    ],
  },
  {
    id: 'asset',
    icon: '🚒',
    label: 'Asset',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        layerKey: 'sar-aviation',
        prefKey: 'aviationBases',
        label: 'SAR Aviation',
        swatchClass: 'bg-emerald-500',
        title: 'RCAF SAR wings, air-ambulance, ISR + contractor aviation bases',
      },
      {
        layerKey: 'fire-stations',
        prefKey: 'fireStations',
        label: 'Fire Stations',
        swatchClass: 'bg-red-500',
        title: 'OSM national backbone + municipal open data',
      },
      {
        layerKey: 'wildfire-bases',
        prefKey: 'wildfireBases',
        label: 'Wildfire / Tanker Bases',
        swatchClass: 'bg-orange-500',
        title: 'Provincial wildfire & air-tanker bases',
      },
      {
        label: 'Hydro Repair Crews',
        swatchClass: 'bg-lime-400',
        comingInPhase: 'R.1c',
        title: 'Utility repair-crew positions (no public AVL feed yet)',
      },
      {
        layerKey: 'ontario-corps',
        prefKey: 'ontarioCorps',
        label: 'Ontario Corps',
        swatchClass: 'bg-yellow-400',
        title: 'Illustrative MEPR showcase — example partner locations',
        special: 'ontarioCorps',
      },
      {
        label: 'EMS',
        swatchClass: 'bg-rose-400',
        comingInPhase: 'R.1',
        title: 'Emergency medical service stations',
      },
      {
        layerKey: 'drone-operator-coverage',
        prefKey: 'droneCoverage',
        label: 'Drone Operator Coverage',
        swatchClass: 'bg-violet-400',
        title: 'Tactical drone providers — drive radius, area coverage, flight + launch time',
      },
      {
        layerKey: 'staging-areas',
        prefKey: 'stagingAreas',
        label: 'Staging Areas',
        swatchClass: 'bg-amber-300',
        title: 'IMS staging areas — forward operating / receiving sites for committed resources',
      },
      {
        layerKey: 'sectors',
        prefKey: 'sectors',
        label: 'Sectors',
        // Law 4 ambient retune (sectorStyles.SECTOR_OPEN_COLOR #a98f3d — that
        // palette explicitly rules out the vivid #f59e0b this swatch was).
        swatchClass: 'bg-[#a98f3d]',
        title:
          'Operator-drawn geographic sectors on an incident (IMS §5.3 divisions). Dashed ambient boundary.',
      },
      {
        // R5 Slice 3 (#507) — the §4.6 Area Command oversight marker. The row
        // label is AREA_COMMAND_HEADING + the LIVE count ("Area Command (2)"),
        // composed in LayerToggle via areaCommandLayerLabel() so the count shows
        // even while the layer is OFF (the gate's PEOC first-notice mitigation).
        layerKey: 'area-command',
        prefKey: 'areaCommand',
        label: AREA_COMMAND_HEADING,
        // Ambient steel — the CAHS framed-asset ink (assetStyles.CAHS_ASSET_AMBIENT_INK).
        swatchClass: 'bg-[#a7b0bd]',
        title:
          'Area Command oversight markers (Ontario IMS §4.6) — each groups its live child incidents',
      },
    ],
  },
  {
    id: 'context',
    icon: '🛰️',
    label: 'Context',
    selection: 'multi',
    collapsible: true,
    defaultCollapsed: true,
    layers: [
      {
        layerKey: 'weather-stations',
        prefKey: 'sensors',
        label: 'Weather Stations',
        swatchClass: 'bg-violet-400',
        title: 'ECCC SWOB weather stations (QA / raw data)',
      },
      {
        layerKey: 'provincial-boundaries',
        prefKey: 'provincialBoundaries',
        label: 'Provincial Boundaries',
        swatchClass: 'bg-slate-400',
        title: 'Provincial boundary polylines (base-map furniture)',
      },
    ],
  },
];

/** Radio siblings of a layer within its group (the real toggleable ones to turn
 *  off when this layer is switched on). Excludes the layer itself + placeholders. */
export function radioSiblings(group: LayerGroupDef, self: LayerDef): BooleanPrefKey[] {
  if (group.selection !== 'radio') return [];
  // A radio-exempt layer (the EWW mute toggle) neither deselects siblings when
  // it's enabled, nor is it deselected when a real radio sibling is enabled.
  if (self.radioExempt) return [];
  return group.layers
    .filter((l) => l !== self && l.prefKey && !l.radioExempt)
    .map((l) => l.prefKey as BooleanPrefKey);
}
